import { c as _c } from "react/compiler-runtime";
import type { PaymentMethod, PaymentStatus } from '@repo/schema/bill';
import { Field, FieldContent, FieldError, FieldLabel, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@repo/ui/components';
import { ReadOnlyField, useReadOnly } from '../../components/readonly.tsx';
import { useFieldContext } from '../../lib/form-context.ts';
const PAYMENT_METHOD_OPTIONS = [{
  label: 'Regular transfer',
  value: 'regular_transfer'
}, {
  label: 'CX Smart',
  value: 'cx_smart'
}, {
  label: 'Proforma',
  value: 'proforma'
}] as const satisfies ReadonlyArray<{
  label: string;
  value: PaymentMethod;
}>;
const PAYMENT_STATUS_OPTIONS = [{
  label: 'Unpaid',
  value: 'unpaid'
}, {
  label: 'Paid',
  value: 'paid'
}] as const satisfies ReadonlyArray<{
  label: string;
  value: PaymentStatus;
}>;
type PaymentSelectProps = {
  'data-testid'?: string;
  disabled?: boolean;
  label?: string;
  placeholder?: string;
};
const PaymentSelect = t0 => {
  const $ = _c(39);
  const {
    "data-testid": dataTestId,
    disabled: t1,
    label,
    options,
    placeholder: t2
  } = t0;
  const disabled = t1 === undefined ? false : t1;
  const placeholder = t2 === undefined ? "Not set" : t2;
  const field = useFieldContext();
  let t3;
  if ($[0] !== placeholder) {
    t3 = {
      label: placeholder,
      value: null
    };
    $[0] = placeholder;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== options || $[3] !== t3) {
    t4 = [t3, ...options];
    $[2] = options;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const items = t4;
  const isReadOnly = useReadOnly();
  let t5;
  if ($[5] !== field || $[6] !== label) {
    t5 = label && <FieldLabel htmlFor={field.name}>{label}</FieldLabel>;
    $[5] = field;
    $[6] = label;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const fieldLabel = t5;
  if (isReadOnly) {
    let t6;
    if ($[8] !== field || $[9] !== options) {
      t6 = options.find(option => option.value === field.state.value)?.label ?? "-";
      $[8] = field;
      $[9] = options;
      $[10] = t6;
    } else {
      t6 = $[10];
    }
    let t7;
    if ($[11] !== fieldLabel || $[12] !== t6) {
      t7 = <ReadOnlyField label={fieldLabel}>{t6}</ReadOnlyField>;
      $[11] = fieldLabel;
      $[12] = t6;
      $[13] = t7;
    } else {
      t7 = $[13];
    }
    return t7;
  }
  let t6;
  if ($[14] !== field) {
    t6 = value => field.handleChange(value);
    $[14] = field;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== placeholder) {
    t7 = <SelectValue placeholder={placeholder} data-sentry-element="SelectValue" data-sentry-source-file="form-payment-select.tsx" />;
    $[16] = placeholder;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== dataTestId || $[19] !== field.name || $[20] !== t7) {
    t8 = <SelectTrigger className="w-full justify-between gap-2" data-testid={dataTestId} id={field.name} data-sentry-element="SelectTrigger" data-sentry-source-file="form-payment-select.tsx">{t7}</SelectTrigger>;
    $[18] = dataTestId;
    $[19] = field.name;
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== items) {
    t9 = <SelectContent data-sentry-element="SelectContent" data-sentry-source-file="form-payment-select.tsx">{items.map(_temp)}</SelectContent>;
    $[22] = items;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== disabled || $[25] !== field.state.value || $[26] !== items || $[27] !== t6 || $[28] !== t8 || $[29] !== t9) {
    t10 = <Select disabled={disabled} items={items} onValueChange={t6} value={field.state.value} data-sentry-element="Select" data-sentry-source-file="form-payment-select.tsx">{t8}{t9}</Select>;
    $[24] = disabled;
    $[25] = field.state.value;
    $[26] = items;
    $[27] = t6;
    $[28] = t8;
    $[29] = t9;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== fieldLabel || $[32] !== t10) {
    t11 = <FieldContent data-sentry-element="FieldContent" data-sentry-source-file="form-payment-select.tsx">{fieldLabel}{t10}</FieldContent>;
    $[31] = fieldLabel;
    $[32] = t10;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] !== field.state.meta.errors) {
    t12 = <FieldError className="text-sm" errors={field.state.meta.errors} data-sentry-element="FieldError" data-sentry-source-file="form-payment-select.tsx" />;
    $[34] = field.state.meta.errors;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  let t13;
  if ($[36] !== t11 || $[37] !== t12) {
    t13 = <Field className="gap-1" data-sentry-element="Field" data-sentry-component="PaymentSelect" data-sentry-source-file="form-payment-select.tsx">{t11}{t12}</Field>;
    $[36] = t11;
    $[37] = t12;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  return t13;
};
export const FormPaymentMethodSelect = props => {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <PaymentSelect {...props} options={PAYMENT_METHOD_OPTIONS} data-sentry-element="PaymentSelect" data-sentry-component="FormPaymentMethodSelect" data-sentry-source-file="form-payment-select.tsx" />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};
export const FormPaymentStatusSelect = props => {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <PaymentSelect {...props} options={PAYMENT_STATUS_OPTIONS} data-sentry-element="PaymentSelect" data-sentry-component="FormPaymentStatusSelect" data-sentry-source-file="form-payment-select.tsx" />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};
function _temp(option_0) {
  return <SelectItem key={option_0.value ?? "none"} value={option_0.value}>{option_0.label}</SelectItem>;
}